import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  ShieldCheck, Archive, Trash2, Gavel, ClipboardList, Loader2, AlertTriangle,
  CheckCircle2, X, Play, FileCheck2, Database, Eye, RotateCcw, Download, Paperclip,
  TrendingUp, History, FileText, Award, HardDrive, Plus, Upload, Target, Bell, Calendar,
} from "lucide-react";

const TABS = [
  { key: "executive", label: "Executive", icon: Award },
  { key: "dashboard", label: "Dashboard", icon: Database },
  { key: "classifications", label: "Classifications", icon: ShieldCheck },
  { key: "policies", label: "Policies", icon: ClipboardList },
  { key: "holds", label: "Legal Holds", icon: Gavel },
  { key: "overrides", label: "Overrides", icon: Plus },
  { key: "jobs", label: "Jobs", icon: Play },
  { key: "reports", label: "Reports", icon: FileText },
  { key: "audit", label: "Audit Trail", icon: FileCheck2 },
];

export default function AdminRetention() {
  const { can } = useAuth();
  const [tab, setTab] = useState("executive");

  if (!can("retention.view")) {
    return (
      <div className="p-12 text-center text-[#1E3A8A]" data-testid="retention-forbidden">
        You don&apos;t have permission to view the data retention framework.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="retention-page">
      <div>
        <div className="hw-eyebrow mb-2">Information lifecycle</div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">
          Data Retention
        </h1>
        <p className="text-sm text-[#334155] mt-2 max-w-3xl">
          Single source of truth for how every collection, document, file, and log on the platform is
          classified, retained, archived, and securely destroyed. HIPAA-aligned defaults; admin-configurable
          policies; legal holds suspend deletion; every action is audited.
        </p>
      </div>

      <div className="flex gap-1 flex-wrap border-b border-[#E5E5E0]" data-testid="retention-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`retention-tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                active
                  ? "border-[#1E3A8A] text-[#1E3A8A]"
                  : "border-transparent text-[#7E937D] hover:text-[#1E3A8A]"
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      <div>
        {tab === "executive" && <ExecutiveTab />}
        {tab === "dashboard" && <DashboardTab />}
        {tab === "classifications" && <ClassificationsTab />}
        {tab === "policies" && <PoliciesTab />}
        {tab === "holds" && <LegalHoldsTab />}
        {tab === "overrides" && <OverridesTab />}
        {tab === "jobs" && <JobsTab />}
        {tab === "reports" && <ReportsTab />}
        {tab === "audit" && <AuditTab />}
      </div>
    </div>
  );
}

// =========================================================================
// Dashboard tab
// =========================================================================

function KpiCard({ icon: Icon, label, value, tone = "default", testId }) {
  return (
    <div
      data-testid={testId}
      className={`bg-white border rounded-2xl p-5 ${
        tone === "warn" ? "border-[#FCD34D] bg-[#FFFBEB]" : tone === "danger" ? "border-red-200 bg-red-50" : "border-[#E5E5E0]"
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
          tone === "warn" ? "bg-[#FCD34D]/30 text-[#92400E]" : tone === "danger" ? "bg-red-100 text-red-700" : "bg-[#E0E7FF] text-[#1E3A8A]"
        }`}>
          <Icon className="w-5 h-5" strokeWidth={1.75} />
        </div>
        <div>
          <div className="hw-eyebrow text-[10px]">{label}</div>
          <div className="font-display text-2xl font-semibold text-[#0F172A] mt-1">{value}</div>
        </div>
      </div>
    </div>
  );
}

function DashboardTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/retention/dashboard");
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-sm text-[#1E3A8A]">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="retention-dashboard">
      <div className="flex justify-end">
        <button onClick={load} disabled={loading} data-testid="retention-dashboard-refresh" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-xs text-[#1E3A8A] hover:bg-[#F9F8F6]">
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Refresh
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <KpiCard icon={Database} label="Record types defined" value={data.total_record_types} testId="kpi-record-types" />
        <KpiCard icon={ShieldCheck} label="Collections classified" value={data.classified_collections} testId="kpi-classified" />
        <KpiCard icon={Gavel} label="Active legal holds" value={data.active_legal_holds} tone={data.active_legal_holds > 0 ? "warn" : "default"} testId="kpi-holds" />
        <KpiCard icon={Archive} label="Due for archive" value={data.due_for_archive} tone={data.due_for_archive > 0 ? "warn" : "default"} testId="kpi-due-archive" />
        <KpiCard icon={Trash2} label="Due for destruction" value={data.due_for_destruction} tone={data.due_for_destruction > 0 ? "danger" : "default"} testId="kpi-due-destroy" />
        <KpiCard icon={Paperclip} label="Attachment orphans" value={data.orphan_attachments ?? 0} tone={(data.orphan_attachments || 0) > 0 ? "warn" : "default"} testId="kpi-orphans" />
        <KpiCard icon={Play} label="Pending jobs" value={data.pending_jobs} testId="kpi-jobs-pending" />
      </div>

      {(data.unclassified_collections || []).length > 0 && (
        <div className="bg-white border border-[#FCD34D] rounded-2xl p-5" data-testid="unclassified-warning">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-[#92400E] mt-0.5" />
            <div>
              <h3 className="font-display font-semibold text-[#92400E]">Unclassified collections found</h3>
              <p className="text-xs text-[#92400E] mt-1">
                These Mongo collections aren&apos;t mapped to any record type. New features should call <code className="font-mono bg-white/60 px-1 rounded">register_collection()</code> or be added to <code className="font-mono bg-white/60 px-1 rounded">retention/classification.py</code>.
              </p>
              <ul className="mt-2 flex flex-wrap gap-1.5">
                {data.unclassified_collections.map((c) => (
                  <li key={c} className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-white border border-[#FCD34D] text-[#92400E]">{c}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =========================================================================
// Classifications tab
// =========================================================================

function ClassificationsTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/retention/registry").then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const grouped = useMemo(() => {
    if (!data) return {};
    const out = {};
    for (const rt of data.record_types) {
      const key = rt.sensitivity;
      out[key] = out[key] || [];
      out[key].push(rt);
    }
    return out;
  }, [data]);

  if (!data) return <div className="text-sm text-[#1E3A8A]">Loading…</div>;

  const SENSITIVITY_TONE = {
    phi: "bg-red-50 border-red-200 text-red-800",
    pii: "bg-orange-50 border-orange-200 text-orange-800",
    financial: "bg-purple-50 border-purple-200 text-purple-800",
    security: "bg-blue-50 border-blue-200 text-blue-800",
    operations: "bg-emerald-50 border-emerald-200 text-emerald-800",
    transient: "bg-slate-50 border-slate-200 text-slate-700",
    admin: "bg-amber-50 border-amber-200 text-amber-800",
  };

  return (
    <div className="space-y-6" data-testid="classifications-tab">
      {Object.entries(grouped).map(([sens, items]) => (
        <div key={sens} className="bg-white border border-[#E5E5E0] rounded-2xl overflow-hidden">
          <div className={`px-5 py-3 border-b border-[#E5E5E0] flex items-center justify-between ${SENSITIVITY_TONE[sens] || ""}`}>
            <h3 className="font-display font-semibold capitalize">{data.sensitivity_levels[sens] || sens}</h3>
            <span className="text-xs">{items.length} record type{items.length === 1 ? "" : "s"}</span>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-[#F9F8F6] text-[#1E3A8A] text-left">
              <tr>
                <th className="px-4 py-2 font-semibold">Record Type</th>
                <th className="px-4 py-2 font-semibold">Collections</th>
                <th className="px-4 py-2 font-semibold">Default retention</th>
                <th className="px-4 py-2 font-semibold">Owner module</th>
              </tr>
            </thead>
            <tbody>
              {items.map((rt) => (
                <tr key={rt.key} className="border-t border-[#F0EFEB] align-top" data-testid={`rt-row-${rt.key}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#0F172A]">{rt.label}</div>
                    <div className="text-[11px] text-[#7E937D] mt-0.5">{rt.description}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-[11px] text-[#1E3A8A]">{(rt.collections || []).join(", ")}</td>
                  <td className="px-4 py-3 text-[#0F172A]">
                    {rt.default_retention_years === 0 ? <span className="italic text-[#7E937D]">Indefinite</span> : `${rt.default_retention_years} years`}
                  </td>
                  <td className="px-4 py-3 font-mono text-[11px] text-[#334155]">{rt.owner_module}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

// =========================================================================
// Policies tab
// =========================================================================

function PoliciesTab() {
  const { can } = useAuth();
  const [policies, setPolicies] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/retention/policies");
      setPolicies(data.policies);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!policies) return <div className="text-sm text-[#1E3A8A]">Loading…</div>;

  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl overflow-hidden" data-testid="policies-tab">
      <table className="w-full text-xs">
        <thead className="bg-[#F9F8F6] text-[#1E3A8A] text-left">
          <tr>
            <th className="px-4 py-2 font-semibold">Record type</th>
            <th className="px-4 py-2 font-semibold">Sensitivity</th>
            <th className="px-4 py-2 font-semibold">Retention</th>
            <th className="px-4 py-2 font-semibold">After retention</th>
            <th className="px-4 py-2 font-semibold">Grace days</th>
            <th className="px-4 py-2 font-semibold">2-person destroy</th>
            <th className="px-4 py-2 font-semibold">Enabled</th>
            <th className="px-4 py-2 font-semibold"></th>
          </tr>
        </thead>
        <tbody>
          {policies.map((p) => (
            <tr key={p.record_type_key} className="border-t border-[#F0EFEB]" data-testid={`policy-row-${p.record_type_key}`}>
              <td className="px-4 py-3">
                <div className="font-semibold text-[#0F172A]">{p.label}</div>
                <div className="text-[11px] text-[#7E937D] font-mono">{p.record_type_key}</div>
              </td>
              <td className="px-4 py-3 capitalize">{p.sensitivity}</td>
              <td className="px-4 py-3">{p.retention_years === 0 ? <span className="italic text-[#7E937D]">Indefinite</span> : `${p.retention_years}y`}</td>
              <td className="px-4 py-3 capitalize">{(p.archive_action || "soft_archive").replace("_", " ")}</td>
              <td className="px-4 py-3">{p.archive_grace_days}d</td>
              <td className="px-4 py-3">{p.requires_two_person_destroy ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : "—"}</td>
              <td className="px-4 py-3">{p.enabled ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <X className="w-4 h-4 text-red-600" />}</td>
              <td className="px-4 py-3 text-right">
                {can("retention.manage") && (
                  <button onClick={() => setEditing(p)} data-testid={`policy-edit-${p.record_type_key}`} className="text-[11px] text-[#1E3A8A] underline decoration-dotted">Edit</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {editing && (
        <PolicyEditor
          policy={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function PolicyEditor({ policy, onClose, onSaved }) {
  const [form, setForm] = useState({
    retention_years: policy.retention_years,
    archive_action: policy.archive_action || "soft_archive",
    archive_grace_days: policy.archive_grace_days,
    requires_two_person_destroy: !!policy.requires_two_person_destroy,
    enabled: !!policy.enabled,
    reason: "",
  });
  const [history, setHistory] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get(`/retention/policies/${policy.record_type_key}/history`).then(({ data }) => setHistory(data.versions || []));
  }, [policy.record_type_key]);
  const submit = async () => {
    if (!form.reason || form.reason.trim().length < 5) {
      toast.error("Please provide a reason for the change (≥5 chars) — needed for version history.");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/retention/policies/${policy.record_type_key}`, form);
      toast.success("Policy updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0F172A]/40" onClick={onClose} data-testid="policy-editor">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[#E5E5E0] flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="hw-eyebrow text-[10px]">Edit policy · v{policy.version || 1}</div>
            <h2 className="font-display text-lg font-semibold">{policy.label}</h2>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="grid md:grid-cols-2 gap-0">
          <div className="p-5 space-y-3 text-sm">
            <Field label="Retention (years)" hint="0 = retain indefinitely (no archive or destroy)">
              <input type="number" min={0} max={100} value={form.retention_years} onChange={(e) => setForm({ ...form, retention_years: Number(e.target.value) || 0 })} data-testid="policy-input-years" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0]" />
            </Field>
            <Field label="When retention is reached">
              <select value={form.archive_action} onChange={(e) => setForm({ ...form, archive_action: e.target.value })} data-testid="policy-input-action" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0]">
                <option value="soft_archive">Soft archive (recoverable during grace window)</option>
                <option value="hard_destroy">Skip archive — destroy immediately (admin-approved)</option>
              </select>
            </Field>
            <Field label="Archive grace days" hint="How long an archived record stays restorable before becoming eligible for destruction.">
              <input type="number" min={0} max={3650} value={form.archive_grace_days} onChange={(e) => setForm({ ...form, archive_grace_days: Number(e.target.value) || 0 })} data-testid="policy-input-grace" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0]" />
            </Field>
            <label className="flex items-start gap-2 text-xs">
              <input type="checkbox" checked={form.requires_two_person_destroy} onChange={(e) => setForm({ ...form, requires_two_person_destroy: e.target.checked })} data-testid="policy-input-2person" />
              <span><strong>Two-person rule for destruction</strong> — the proposer cannot also approve. Strongly recommended for PHI.</span>
            </label>
            <label className="flex items-start gap-2 text-xs">
              <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="policy-input-enabled" />
              <span><strong>Policy enabled</strong> — when off, this record type is excluded from all retention jobs.</span>
            </label>
            <Field label="Reason for this change" hint="Required for the audit + version history.">
              <textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="policy-input-reason" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0]" />
            </Field>
          </div>
          <div className="p-5 border-l border-[#E5E5E0] bg-[#F9F8F6] space-y-3 text-xs">
            <div className="hw-eyebrow text-[10px] flex items-center gap-1"><History className="w-3 h-3" /> Version history</div>
            {!history ? (
              <div className="text-[#7E937D]">Loading history…</div>
            ) : history.length === 0 ? (
              <div className="text-[#7E937D]">No prior versions.</div>
            ) : (
              <ul className="space-y-2" data-testid="policy-history">
                {history.map((v) => (
                  <li key={v.id} className="bg-white border border-[#E5E5E0] rounded-xl p-3 text-[11px]">
                    <div className="flex items-center justify-between">
                      <strong>v{v.version}</strong>
                      <span className="text-[#7E937D]">{v.ts ? new Date(v.ts).toLocaleString() : ""}</span>
                    </div>
                    <div className="text-[10px] text-[#1E3A8A]">by {v.actor_email || v.actor_id}</div>
                    {v.reason && <div className="text-[#334155] mt-1 italic">&quot;{v.reason}&quot;</div>}
                    {v.previous_snapshot && (
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <div className="bg-red-50 border border-red-100 rounded p-2">
                          <div className="text-[9px] uppercase tracking-wide text-red-700 mb-1">Before</div>
                          {Object.entries(v.previous_snapshot).map(([k, val]) => (
                            <div key={k}>{k.replace(/_/g, " ")}: <strong>{String(val)}</strong></div>
                          ))}
                        </div>
                        <div className="bg-emerald-50 border border-emerald-100 rounded p-2">
                          <div className="text-[9px] uppercase tracking-wide text-emerald-700 mb-1">After</div>
                          {Object.entries(v.snapshot).map(([k, val]) => (
                            <div key={k}>{k.replace(/_/g, " ")}: <strong>{String(val)}</strong></div>
                          ))}
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="px-5 py-4 bg-[#F9F8F6] border-t border-[#E5E5E0] flex justify-end gap-2">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 rounded-full text-xs border border-[#E5E5E0] bg-white">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="policy-save" className="px-4 py-2 rounded-full text-xs bg-[#1E3A8A] text-white disabled:opacity-50">
            {saving ? "Saving…" : "Save policy"}
          </button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-xs font-medium text-[#334155] mb-1">{label}</label>
    {children}
    {hint && <div className="text-[10px] text-[#7E937D] mt-1">{hint}</div>}
  </div>
);

// =========================================================================
// Legal Holds tab
// =========================================================================

function LegalHoldsTab() {
  const { can } = useAuth();
  const [holds, setHolds] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const load = useCallback(async () => {
    const { data } = await api.get("/retention/legal-holds", { params: { active_only: !showAll } });
    setHolds(data.holds);
  }, [showAll]);
  useEffect(() => { load(); }, [load]);
  const release = async (id) => {
    if (!window.confirm("Release this legal hold? Records will become eligible for the normal retention lifecycle again.")) return;
    try {
      await api.delete(`/retention/legal-holds/${id}`);
      toast.success("Hold released");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!holds) return <div className="text-sm text-[#1E3A8A]">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="holds-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="text-xs text-[#334155]">{holds.length} {showAll ? "total" : "active"} hold{holds.length === 1 ? "" : "s"}</div>
          <label className="inline-flex items-center gap-1.5 text-[11px] text-[#1E3A8A] cursor-pointer">
            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} data-testid="hold-show-all" />
            Show released holds
          </label>
        </div>
        {can("retention.manage") && (
          <button onClick={() => setShowForm(true)} data-testid="hold-new-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-xs">
            <Gavel className="w-3.5 h-3.5" /> Add legal hold
          </button>
        )}
      </div>
      {holds.length === 0 ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-8 text-center text-sm text-[#7E937D]">
          No legal holds in place.
        </div>
      ) : (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-[#F9F8F6] text-[#1E3A8A] text-left">
              <tr>
                <th className="px-4 py-2 font-semibold">Record type</th>
                <th className="px-4 py-2 font-semibold">Scope</th>
                <th className="px-4 py-2 font-semibold">Reason</th>
                <th className="px-4 py-2 font-semibold">Imposed by</th>
                <th className="px-4 py-2 font-semibold">When</th>
                <th className="px-4 py-2 font-semibold">Status</th>
                <th className="px-4 py-2 font-semibold"></th>
              </tr>
            </thead>
            <tbody>
              {holds.map((h) => (
                <tr key={h.id} className="border-t border-[#F0EFEB]" data-testid={`hold-row-${h.id}`}>
                  <td className="px-4 py-3 font-semibold">{h.record_type_key}</td>
                  <td className="px-4 py-3">{h.scope}{h.scope === "record" && <span className="text-[10px] text-[#7E937D] block font-mono">{h.collection}/{h.record_id?.slice(0, 8)}…</span>}</td>
                  <td className="px-4 py-3 max-w-[260px]">{h.reason}</td>
                  <td className="px-4 py-3 text-[11px]">{h.created_by_email || "—"}</td>
                  <td className="px-4 py-3 text-[11px]">{h.created_at ? new Date(h.created_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3">
                    {h.active ? <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">ACTIVE</span> : <span className="text-[10px] text-[#7E937D]">released</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {h.active && can("retention.manage") && (
                      <button onClick={() => release(h.id)} data-testid={`hold-release-${h.id}`} className="text-[11px] text-red-700 underline decoration-dotted">Release</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showForm && <HoldForm onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function HoldForm({ onClose, onCreated }) {
  const [registry, setRegistry] = useState(null);
  const [form, setForm] = useState({ record_type_key: "", scope: "record_type", collection: "", record_id: "", reason: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/retention/registry").then(({ data }) => setRegistry(data)); }, []);
  const submit = async () => {
    if (!form.record_type_key || !form.reason) {
      toast.error("Record type and reason are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/retention/legal-holds", form);
      toast.success("Legal hold imposed");
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0F172A]/40" onClick={onClose} data-testid="hold-form">
      <div className="bg-white rounded-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[#E5E5E0] flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold">New legal hold</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <Field label="Record type">
            <select value={form.record_type_key} onChange={(e) => setForm({ ...form, record_type_key: e.target.value })} data-testid="hold-rt" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0]">
              <option value="">— pick a record type —</option>
              {(registry?.record_types || []).map((rt) => (
                <option key={rt.key} value={rt.key}>{rt.label} ({rt.sensitivity})</option>
              ))}
            </select>
          </Field>
          <Field label="Scope">
            <select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} data-testid="hold-scope" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0]">
              <option value="record_type">Hold ALL records of this type</option>
              <option value="record">Hold ONE specific record</option>
            </select>
          </Field>
          {form.scope === "record" && (
            <>
              <Field label="Collection name">
                <input value={form.collection} onChange={(e) => setForm({ ...form, collection: e.target.value })} data-testid="hold-collection" placeholder="e.g. care_seekers" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] font-mono text-xs" />
              </Field>
              <Field label="Record id (UUID)">
                <input value={form.record_id} onChange={(e) => setForm({ ...form, record_id: e.target.value })} data-testid="hold-record-id" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] font-mono text-xs" />
              </Field>
            </>
          )}
          <Field label="Reason / case number" hint="Why is this hold being imposed? Required for audit.">
            <textarea rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="hold-reason" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0]" />
          </Field>
        </div>
        <div className="px-5 py-4 bg-[#F9F8F6] border-t border-[#E5E5E0] flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-xs border border-[#E5E5E0] bg-white">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="hold-submit" className="px-4 py-2 rounded-full text-xs bg-[#1E3A8A] text-white disabled:opacity-50">{saving ? "Saving…" : "Impose hold"}</button>
        </div>
      </div>
    </div>
  );
}

// =========================================================================
// Jobs tab
// =========================================================================

function JobsTab() {
  const { can } = useAuth();
  const [jobs, setJobs] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewAction, setPreviewAction] = useState("archive");
  const [running, setRunning] = useState(false);
  const load = useCallback(async () => {
    const { data } = await api.get("/retention/jobs");
    setJobs(data.jobs);
  }, []);
  useEffect(() => { load(); }, [load]);
  const runDryRun = async () => {
    setRunning(true);
    setPreview(null);
    try {
      const { data } = await api.post("/retention/jobs/dry-run", { action: previewAction });
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRunning(false); }
  };
  const propose = async () => {
    setRunning(true);
    try {
      await api.post("/retention/jobs", { action: previewAction });
      toast.success("Job proposed — awaiting approval");
      setPreview(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRunning(false); }
  };
  const approve = async (id) => {
    try {
      await api.post(`/retention/jobs/${id}/approve`);
      toast.success("Job approved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const reject = async (id) => {
    const reason = window.prompt("Reject reason (optional):") || "";
    try {
      await api.post(`/retention/jobs/${id}/reject`, { reason });
      toast.success("Job rejected");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const execute = async (id) => {
    if (!window.confirm("Execute this retention job? This will modify the database.")) return;
    try {
      const { data } = await api.post(`/retention/jobs/${id}/execute`);
      toast.success(`Job executed — ${JSON.stringify(data.result || {})}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="jobs-tab">
      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-5">
        <h3 className="font-display font-semibold text-[#0F172A]">Dry-run preview</h3>
        <p className="text-xs text-[#7E937D] mt-1">See what a retention job would touch before proposing it. Dry-runs are read-only — they never modify data. The system also runs this scan automatically every night and emails admins if anything is due.</p>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <select value={previewAction} onChange={(e) => setPreviewAction(e.target.value)} data-testid="dryrun-action" className="px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm bg-white">
            <option value="archive">Archive (records past retention window)</option>
            <option value="destroy">Destroy (records archived past grace window)</option>
          </select>
          <button onClick={runDryRun} disabled={running} data-testid="dryrun-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#E0E7FF] text-[#1E3A8A] text-xs font-semibold disabled:opacity-50">
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />} Run dry-run
          </button>
          {preview && can("retention.manage") && (
            <button onClick={propose} disabled={running || preview.total_due === 0} data-testid="propose-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-xs font-semibold disabled:opacity-50">
              <Play className="w-3.5 h-3.5" /> Propose job ({preview.total_due} records)
            </button>
          )}
        </div>
        {preview && <PreviewTable preview={preview} />}
      </div>

      <AttachmentSweepCard />
      <ManifestReconcileCard />

      <div>
        <h3 className="font-display font-semibold text-[#0F172A] mb-3">Jobs history</h3>
        {!jobs ? <div className="text-sm text-[#1E3A8A]">Loading…</div> : jobs.length === 0 ? (
          <div className="bg-white border border-[#E5E5E0] rounded-2xl p-8 text-center text-sm text-[#7E937D]">No jobs yet — run a dry-run to get started.</div>
        ) : (
          <div className="bg-white border border-[#E5E5E0] rounded-2xl overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-[#F9F8F6] text-[#1E3A8A] text-left">
                <tr>
                  <th className="px-4 py-2 font-semibold">When</th>
                  <th className="px-4 py-2 font-semibold">Action</th>
                  <th className="px-4 py-2 font-semibold">Records</th>
                  <th className="px-4 py-2 font-semibold">Proposed by</th>
                  <th className="px-4 py-2 font-semibold">Status</th>
                  <th className="px-4 py-2 font-semibold">2-person?</th>
                  <th className="px-4 py-2 font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.id} className="border-t border-[#F0EFEB]" data-testid={`job-row-${j.id}`}>
                    <td className="px-4 py-3">{j.created_at ? new Date(j.created_at).toLocaleString() : "—"}</td>
                    <td className="px-4 py-3 capitalize font-semibold">{j.action}</td>
                    <td className="px-4 py-3">{j.preview?.total_due ?? "—"}</td>
                    <td className="px-4 py-3 text-[11px]">{j.proposed_by_email || "—"}</td>
                    <td className="px-4 py-3"><JobStatusBadge status={j.status} /></td>
                    <td className="px-4 py-3">{j.requires_two_person ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : "—"}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {j.status === "proposed" && can("retention.approve") && (
                        <>
                          <button onClick={() => approve(j.id)} data-testid={`job-approve-${j.id}`} className="text-[11px] text-emerald-700 underline decoration-dotted mr-3">Approve</button>
                          <button onClick={() => reject(j.id)} data-testid={`job-reject-${j.id}`} className="text-[11px] text-red-700 underline decoration-dotted">Reject</button>
                        </>
                      )}
                      {j.status === "approved" && can("retention.execute") && (
                        <button onClick={() => execute(j.id)} data-testid={`job-execute-${j.id}`} className="text-[11px] text-[#1E3A8A] underline decoration-dotted">Execute</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function PreviewTable({ preview }) {
  const hits = (preview.results || []).filter((r) => (r.due_count || 0) > 0 || r.skipped_reason);
  return (
    <div className="mt-4 border border-[#E5E5E0] rounded-xl overflow-hidden" data-testid="preview-table">
      <div className="px-4 py-2 bg-[#F9F8F6] text-xs flex items-center justify-between">
        <span><strong>Total impact:</strong> {preview.total_due} records</span>
        <span className="text-[#7E937D] text-[10px]">Computed {new Date(preview.computed_at).toLocaleString()}</span>
      </div>
      <table className="w-full text-xs">
        <thead className="bg-white text-[#1E3A8A] text-left">
          <tr>
            <th className="px-3 py-2 font-semibold">Record type</th>
            <th className="px-3 py-2 font-semibold">Due</th>
            <th className="px-3 py-2 font-semibold">Cutoff</th>
            <th className="px-3 py-2 font-semibold">Notes</th>
          </tr>
        </thead>
        <tbody>
          {hits.length === 0 ? (
            <tr><td colSpan={4} className="px-3 py-4 text-center text-[#7E937D]">Nothing due right now. All records are within their retention window or on legal hold.</td></tr>
          ) : hits.map((r) => (
            <tr key={r.record_type_key} className="border-t border-[#F0EFEB]">
              <td className="px-3 py-2">{r.label || r.record_type_key}</td>
              <td className="px-3 py-2">{r.due_count || 0}</td>
              <td className="px-3 py-2 text-[#7E937D] text-[10px]">{r.cutoff ? new Date(r.cutoff).toLocaleDateString() : "—"}</td>
              <td className="px-3 py-2 text-[#7E937D]">{r.skipped_reason ? <em>skipped: {r.skipped_reason}</em> : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function JobStatusBadge({ status }) {
  const tone = {
    proposed: "bg-blue-100 text-blue-800",
    approved: "bg-amber-100 text-amber-800",
    executing: "bg-purple-100 text-purple-800",
    executed: "bg-emerald-100 text-emerald-800",
    rejected: "bg-slate-200 text-slate-700",
    failed: "bg-red-100 text-red-800",
  }[status] || "bg-slate-100 text-slate-700";
  return <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold ${tone}`}>{status}</span>;
}

// =========================================================================
// Audit tab
// =========================================================================

function AuditTab() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("");
  useEffect(() => { api.get("/retention/audit", { params: { limit: 200 } }).then(({ data }) => setRows(data.audit)); }, []);
  const filtered = useMemo(() => {
    if (!rows) return null;
    if (!filter) return rows;
    const q = filter.toLowerCase();
    return rows.filter((r) =>
      (r.action || "").toLowerCase().includes(q) ||
      (r.record_type_key || "").toLowerCase().includes(q) ||
      (r.actor_email || "").toLowerCase().includes(q)
    );
  }, [rows, filter]);

  const downloadCsv = async () => {
    try {
      const { data } = await api.get("/retention/audit/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `harborwell-retention-audit-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Audit log exported");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!filtered) return <div className="text-sm text-[#1E3A8A]">Loading…</div>;
  return (
    <div className="space-y-3" data-testid="audit-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by action, record type, or email…" data-testid="audit-filter" className="flex-1 min-w-[260px] px-3 py-2 rounded-full border border-[#E5E5E0] text-sm" />
        <button onClick={downloadCsv} data-testid="audit-csv-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-xs font-medium">
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>
      <div className="bg-white border border-[#E5E5E0] rounded-2xl overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-[#F9F8F6] text-[#1E3A8A] text-left">
            <tr>
              <th className="px-4 py-2 font-semibold">When</th>
              <th className="px-4 py-2 font-semibold">Action</th>
              <th className="px-4 py-2 font-semibold">Record type</th>
              <th className="px-4 py-2 font-semibold">Record id</th>
              <th className="px-4 py-2 font-semibold">Actor</th>
              <th className="px-4 py-2 font-semibold">Details</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#7E937D]">No audit entries yet.</td></tr>
            ) : filtered.map((r, i) => (
              <tr key={i} className="border-t border-[#F0EFEB]" data-testid={`audit-row-${i}`}>
                <td className="px-4 py-2 whitespace-nowrap">{r.ts ? new Date(r.ts).toLocaleString() : "—"}</td>
                <td className="px-4 py-2 font-mono text-[11px] uppercase">{r.action}</td>
                <td className="px-4 py-2">{r.record_type_key || "—"}</td>
                <td className="px-4 py-2 font-mono text-[10px] break-all max-w-[160px]">{r.record_id || r.hold_id || r.job_id || "—"}</td>
                <td className="px-4 py-2 text-[11px]">{r.actor_email || "—"}</td>
                <td className="px-4 py-2 text-[#7E937D] text-[10px] max-w-[280px] truncate">{r.reason || r.changes ? JSON.stringify(r.changes || r.reason) : (r.action === "archive" || r.action === "destroy" ? r.collection : "")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AttachmentSweepCard() {
  const { can } = useAuth();
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(false);
  const runDry = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/retention/sweep/attachments/dry-run");
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRunning(false); }
  };
  const execute = async () => {
    if (!window.confirm(`Sweep ${preview.total_orphaned} orphan(s) and ${preview.total_archive_due} inherited-archive attachment(s)? Destroys orphans; archives the inherited ones.`)) return;
    setRunning(true);
    try {
      const { data } = await api.post("/retention/sweep/attachments/execute");
      toast.success(`Swept: ${data.destroyed_orphans} destroyed, ${data.archived_inherited} archived`);
      setPreview(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRunning(false); }
  };
  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl p-5" data-testid="attachment-sweep-card">
      <h3 className="font-display font-semibold text-[#0F172A]">Attachment orphan sweep</h3>
      <p className="text-xs text-[#7E937D] mt-1">
        Finds uploaded files whose parent record was destroyed (orphan → destroy) or archived (inherits archive). Runs alongside the nightly automated scan.
      </p>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <button onClick={runDry} disabled={running} data-testid="sweep-dryrun-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#E0E7FF] text-[#1E3A8A] text-xs font-semibold disabled:opacity-50">
          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />} Run sweep dry-run
        </button>
        {preview && can("retention.execute") && (preview.total_orphaned + preview.total_archive_due) > 0 && (
          <button onClick={execute} disabled={running} data-testid="sweep-execute-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#9F1239] text-white text-xs font-semibold">
            <Trash2 className="w-3.5 h-3.5" /> Execute sweep
          </button>
        )}
      </div>
      {preview && (
        <div className="mt-3 grid grid-cols-2 gap-3 text-xs" data-testid="sweep-preview">
          <div className="px-3 py-2 rounded-xl bg-red-50 border border-red-200">
            <div className="hw-eyebrow text-[10px] text-red-800">Orphans (will destroy)</div>
            <div className="font-display text-xl font-semibold text-red-900 mt-1">{preview.total_orphaned}</div>
          </div>
          <div className="px-3 py-2 rounded-xl bg-amber-50 border border-amber-200">
            <div className="hw-eyebrow text-[10px] text-amber-800">Inherited (will archive)</div>
            <div className="font-display text-xl font-semibold text-amber-900 mt-1">{preview.total_archive_due}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function ManifestReconcileCard() {
  const { can } = useAuth();
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  if (!can("retention.execute")) return null;
  const run = async () => {
    if (!window.confirm("Reconcile destruction manifest now? This re-destroys any record that has reappeared since the last scheduled destruction — typically used after a disaster-recovery restore.")) return;
    setRunning(true);
    try {
      const { data } = await api.post("/retention/reconcile-manifest");
      setResult(data);
      toast.success(`Reconciled ${data.checked_manifests} manifest(s); re-destroyed ${data.re_destroyed} record(s).`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRunning(false); }
  };
  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl p-5" data-testid="manifest-reconcile-card">
      <h3 className="font-display font-semibold text-[#0F172A]">Backup-aware reconciliation</h3>
      <p className="text-xs text-[#7E937D] mt-1">
        Every destruction writes an immutable manifest. On every backend startup — and here on demand — we sweep recent manifests and re-destroy any record that has been resurrected (typically by a database restore from backup).
      </p>
      <div className="mt-3 flex items-center gap-2">
        <button onClick={run} disabled={running} data-testid="manifest-reconcile-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-xs font-semibold disabled:opacity-50">
          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Reconcile now
        </button>
        {result && (
          <span className="text-[11px] text-[#7E937D]">Last run: {result.checked_manifests} manifest(s), {result.re_destroyed} re-destroyed</span>
        )}
      </div>
    </div>
  );
}

// =========================================================================
// Executive Dashboard tab
// =========================================================================

function ExecutiveTab() {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/retention/exec-dashboard");
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="text-sm text-[#1E3A8A]">Loading…</div>;

  const score = data.score;
  const scoreColor = score.score >= 90 ? "#166534" : score.score >= 80 ? "#1E3A8A" : score.score >= 60 ? "#92400E" : "#9F1239";
  const scoreBg = score.score >= 90 ? "#DCFCE7" : score.score >= 80 ? "#E0E7FF" : score.score >= 60 ? "#FEF3C7" : "#FEE2E2";

  const fmtBytes = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 ** 2) return `${(b / 1024).toFixed(1)} KB`;
    if (b < 1024 ** 3) return `${(b / 1024 ** 2).toFixed(1)} MB`;
    return `${(b / 1024 ** 3).toFixed(2)} GB`;
  };

  return (
    <div className="space-y-6" data-testid="executive-tab">
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Compliance score hero */}
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6 lg:col-span-1" data-testid="exec-compliance-score">
          <div className="hw-eyebrow text-[10px] mb-3">Compliance score</div>
          <div className="flex items-center gap-4">
            <div style={{ background: scoreBg, color: scoreColor }} className="w-24 h-24 rounded-full flex items-center justify-center font-display font-bold text-3xl">
              {score.score}
            </div>
            <div>
              <div className="text-xs text-[#7E937D]">Grade</div>
              <div className="font-display text-4xl font-semibold" style={{ color: scoreColor }}>{score.grade}</div>
            </div>
          </div>
          <div className="mt-4 space-y-1.5">
            {Object.entries(score.components).map(([key, c]) => (
              <div key={key} className="flex items-center gap-2 text-[11px]" data-testid={`score-component-${key}`}>
                <div className="flex-1 capitalize text-[#334155]">{key.replace(/_/g, " ")}</div>
                <div className="w-24 h-1.5 bg-[#F0EFEB] rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${c.score}%`, background: c.score >= 80 ? "#166534" : c.score >= 60 ? "#92400E" : "#9F1239" }} />
                </div>
                <div className="w-10 text-right font-mono text-[10px] text-[#1E3A8A]">{c.score}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
          <ExecTile icon={Gavel} label="Records under legal hold" value={data.active_holds.length} tone={data.active_holds.length ? "warn" : "default"} testId="exec-holds" />
          <ExecTile icon={Plus} label="Pending overrides" value={data.pending_overrides} tone={data.pending_overrides ? "warn" : "default"} testId="exec-pending-overrides" />
          <ExecTile icon={X} label="Failed retention jobs" value={data.failed_jobs.length} tone={data.failed_jobs.length ? "danger" : "default"} testId="exec-failed-jobs" />
          <ExecTile icon={HardDrive} label="Total storage" value={fmtBytes(data.storage.total_size_bytes)} sub={`${data.storage.total_records.toLocaleString()} records`} testId="exec-storage" />
        </div>
      </div>

      {data.trends.length > 1 && (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-5" data-testid="exec-trends">
          <h3 className="font-display font-semibold text-[#0F172A] inline-flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Compliance score trend</h3>
          <p className="text-xs text-[#7E937D] mt-1">Last {data.trends.length} months.</p>
          <div className="mt-3 flex items-end gap-2 h-24">
            {data.trends.map((t) => {
              const s = t.report?.compliance_score?.score ?? 0;
              const h = (s / 100) * 100;
              const color = s >= 90 ? "#166534" : s >= 80 ? "#1E3A8A" : s >= 60 ? "#92400E" : "#9F1239";
              return (
                <div key={t.month} className="flex-1 flex flex-col items-center gap-1">
                  <div className="text-[10px] font-semibold" style={{ color }}>{s}</div>
                  <div className="w-full rounded-t" style={{ height: `${h}%`, background: color, minHeight: 4 }} />
                  <div className="text-[9px] text-[#7E937D]">{t.month?.slice(-2)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-5">
          <h3 className="font-display font-semibold text-[#0F172A]">Largest collections by size</h3>
          <ul className="mt-3 space-y-2 text-xs">
            {data.storage.top_collections.map((c) => (
              <li key={c.collection} className="flex items-center gap-3" data-testid={`storage-row-${c.collection}`}>
                <div className="flex-1">
                  <div className="font-mono text-[11px]">{c.collection}</div>
                  <div className="text-[10px] text-[#7E937D]">{c.sensitivity}</div>
                </div>
                <div className="text-right">
                  <div className="font-semibold">{fmtBytes(c.size_bytes)}</div>
                  <div className="text-[10px] text-[#7E937D]">{c.count.toLocaleString()} docs</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-5">
          <h3 className="font-display font-semibold text-[#0F172A]">Audit readiness</h3>
          <ul className="mt-3 space-y-2 text-xs">
            <li className="flex items-center justify-between">
              <span className="text-[#334155]">Last nightly scan</span>
              <span className="font-mono text-[10px]">{data.audit_readiness.last_nightly_scan ? new Date(data.audit_readiness.last_nightly_scan).toLocaleString() : <span className="text-red-700">never</span>}</span>
            </li>
            <li className="flex items-center justify-between">
              <span className="text-[#334155]">Last manifest reconciliation</span>
              <span className="font-mono text-[10px]">{data.audit_readiness.last_reconciliation ? new Date(data.audit_readiness.last_reconciliation).toLocaleString() : <span className="text-red-700">never</span>}</span>
            </li>
            <li className="flex items-center justify-between">
              <span className="text-[#334155]">Coverage</span>
              <span className="font-mono text-[10px]">{data.coverage.classified_collections} / {data.coverage.classified_collections + (data.coverage.unclassified_collections?.length || 0)} collections</span>
            </li>
          </ul>
        </div>
      </div>

      {data.failed_jobs.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5" data-testid="exec-failed-jobs-list">
          <h3 className="font-display font-semibold text-red-900 inline-flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Failed retention jobs (last 5)</h3>
          <ul className="mt-2 space-y-2 text-xs">
            {data.failed_jobs.map((j) => (
              <li key={j.id} className="bg-white border border-red-100 rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <div><strong>{j.action}</strong> · proposed {j.proposed_at?.slice(0, 10)}</div>
                  <span className="text-red-700 font-mono">{j.execute_error?.slice(0, 80) || "no error message"}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <ComplianceTargetCard currentScore={score.score} />
        <MyPrefsCard />
      </div>
    </div>
  );
}

function ExecTile({ icon: Icon, label, value, sub, tone = "default", testId }) {
  const toneClass = tone === "warn" ? "bg-amber-50 border-amber-200" : tone === "danger" ? "bg-red-50 border-red-200" : "bg-white border-[#E5E5E0]";
  return (
    <div className={`border rounded-2xl p-5 ${toneClass}`} data-testid={testId}>
      <div className="flex items-start gap-3">
        <Icon className="w-5 h-5 text-[#1E3A8A]" strokeWidth={1.75} />
        <div>
          <div className="hw-eyebrow text-[10px]">{label}</div>
          <div className="font-display text-2xl font-semibold text-[#0F172A] mt-1">{value}</div>
          {sub && <div className="text-[10px] text-[#7E937D] mt-0.5">{sub}</div>}
        </div>
      </div>
    </div>
  );
}

// =========================================================================
// Overrides tab
// =========================================================================

function OverridesTab() {
  const { can } = useAuth();
  const [overrides, setOverrides] = useState(null);
  const [registry, setRegistry] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const load = useCallback(async () => {
    const { data } = await api.get("/retention/overrides");
    setOverrides(data.overrides);
  }, []);
  useEffect(() => {
    load();
    api.get("/retention/registry").then(({ data }) => setRegistry(data));
  }, [load]);
  const approve = async (id) => {
    try {
      await api.post(`/retention/overrides/${id}/approve`);
      toast.success("Override approved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const reject = async (id) => {
    const reason = window.prompt("Reject reason (optional):") || "";
    try {
      await api.post(`/retention/overrides/${id}/reject`, { reason });
      toast.success("Override rejected");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!overrides) return <div className="text-sm text-[#1E3A8A]">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="overrides-tab">
      <div className="flex items-center justify-between">
        <div className="text-xs text-[#334155] max-w-2xl">
          Record-level retention exceptions. Use <strong>Extend</strong> for litigation holds or special regulatory requirements; <strong>Early destroy</strong> for approved HIPAA right-to-deletion requests. Every override is justified, approved, and audited.
        </div>
        <div className="flex items-center gap-2">
          {can("retention.manage") && (
            <BulkImportButton onComplete={load} />
          )}
          {can("retention.manage") && (
            <button onClick={() => setShowForm(true)} data-testid="override-new-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-xs">
              <Plus className="w-3.5 h-3.5" /> New override
            </button>
          )}
        </div>
      </div>
      {overrides.length === 0 ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-8 text-center text-sm text-[#7E937D]">No record-level overrides yet.</div>
      ) : (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-[#F9F8F6] text-[#1E3A8A] text-left">
              <tr>
                <th className="px-4 py-2 font-semibold">Type</th>
                <th className="px-4 py-2 font-semibold">Record</th>
                <th className="px-4 py-2 font-semibold">Justification</th>
                <th className="px-4 py-2 font-semibold">Proposed by</th>
                <th className="px-4 py-2 font-semibold">Status</th>
                <th className="px-4 py-2 font-semibold"></th>
              </tr>
            </thead>
            <tbody>
              {overrides.map((o) => (
                <tr key={o.id} className="border-t border-[#F0EFEB] align-top" data-testid={`override-row-${o.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold capitalize">{o.override_type.replace("_", " ")}</div>
                    {o.new_retention_until && <div className="text-[10px] text-[#7E937D]">until {o.new_retention_until?.slice(0, 10)}</div>}
                  </td>
                  <td className="px-4 py-3 font-mono text-[10px]">
                    <div>{o.record_type_key}</div>
                    <div className="text-[#7E937D]">{o.collection}/{o.record_id?.slice(0, 8)}…</div>
                  </td>
                  <td className="px-4 py-3 max-w-[280px]">{o.justification}</td>
                  <td className="px-4 py-3 text-[11px]">{o.proposed_by_email}</td>
                  <td className="px-4 py-3"><JobStatusBadge status={o.status} /></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {o.status === "proposed" && can("retention.approve") && (
                      <>
                        <button onClick={() => approve(o.id)} data-testid={`override-approve-${o.id}`} className="text-[11px] text-emerald-700 underline decoration-dotted mr-3">Approve</button>
                        <button onClick={() => reject(o.id)} data-testid={`override-reject-${o.id}`} className="text-[11px] text-red-700 underline decoration-dotted">Reject</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showForm && registry && <OverrideForm registry={registry} onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function OverrideForm({ registry, onClose, onCreated }) {
  const [form, setForm] = useState({
    record_type_key: "",
    collection: "",
    record_id: "",
    override_type: "extend",
    new_retention_until: "",
    justification: "",
  });
  const [saving, setSaving] = useState(false);
  const rt = registry.record_types.find((r) => r.key === form.record_type_key);
  const submit = async () => {
    if (!form.record_type_key || !form.collection || !form.record_id || !form.justification) {
      toast.error("All fields are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/retention/overrides", form);
      toast.success("Override proposed — awaiting approval");
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0F172A]/40" onClick={onClose} data-testid="override-form">
      <div className="bg-white rounded-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[#E5E5E0] flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold">New record-level override</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <Field label="Override type">
            <select value={form.override_type} onChange={(e) => setForm({ ...form, override_type: e.target.value })} data-testid="override-type" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0]">
              <option value="extend">Extend retention (keep longer)</option>
              <option value="early_destroy">Early destroy (e.g. HIPAA erasure request)</option>
            </select>
          </Field>
          <Field label="Record type">
            <select value={form.record_type_key} onChange={(e) => setForm({ ...form, record_type_key: e.target.value, collection: "" })} data-testid="override-rt" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0]">
              <option value="">— pick a record type —</option>
              {registry.record_types.map((r) => <option key={r.key} value={r.key}>{r.label} ({r.sensitivity})</option>)}
            </select>
          </Field>
          {rt && (
            <Field label="Collection">
              <select value={form.collection} onChange={(e) => setForm({ ...form, collection: e.target.value })} data-testid="override-collection" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] font-mono text-xs">
                <option value="">— pick collection —</option>
                {rt.collections.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          )}
          <Field label="Record id (UUID)">
            <input value={form.record_id} onChange={(e) => setForm({ ...form, record_id: e.target.value })} data-testid="override-record-id" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] font-mono text-xs" />
          </Field>
          {form.override_type === "extend" && (
            <Field label="Keep until" hint="ISO date — the engine treats this record as protected until this date.">
              <input type="date" value={form.new_retention_until} onChange={(e) => setForm({ ...form, new_retention_until: e.target.value })} data-testid="override-until" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0]" />
            </Field>
          )}
          <Field label="Justification" hint="Required, ≥10 chars. Becomes part of the audit trail.">
            <textarea rows={3} value={form.justification} onChange={(e) => setForm({ ...form, justification: e.target.value })} data-testid="override-justification" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0]" />
          </Field>
        </div>
        <div className="px-5 py-4 bg-[#F9F8F6] border-t border-[#E5E5E0] flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-xs border border-[#E5E5E0] bg-white">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="override-submit" className="px-4 py-2 rounded-full text-xs bg-[#1E3A8A] text-white disabled:opacity-50">{saving ? "Proposing…" : "Propose override"}</button>
        </div>
      </div>
    </div>
  );
}

// =========================================================================
// Reports tab
// =========================================================================

function ReportsTab() {
  const { can } = useAuth();
  const [reports, setReports] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatingAnnual, setGeneratingAnnual] = useState(false);
  const load = useCallback(async () => {
    const { data } = await api.get("/retention/reports");
    setReports(data.reports);
  }, []);
  useEffect(() => { load(); }, [load]);
  const generateNow = async () => {
    if (!window.confirm("Generate the compliance report for the prior month now? It will also be emailed to admins + Supervisors.")) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/retention/reports/generate", {});
      toast.success(`Report ${data.month} generated`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGenerating(false); }
  };
  const generateAnnual = async () => {
    const yearStr = window.prompt("Generate annual compliance roll-up for which year?", String(new Date().getFullYear() - 1));
    if (!yearStr) return;
    const year = parseInt(yearStr, 10);
    if (!year || year < 2020 || year > 2100) { toast.error("Invalid year"); return; }
    setGeneratingAnnual(true);
    try {
      const { data } = await api.post("/retention/reports/annual/generate", { year, send_email: true });
      toast.success(`Annual report ${data.year} generated`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGeneratingAnnual(false); }
  };
  const download = async (id, format) => {
    try {
      const { data } = await api.get(`/retention/reports/${id}/download?format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `harborwell-compliance-${id}.${format === "pdf" ? "pdf" : "html"}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!reports) return <div className="text-sm text-[#1E3A8A]">Loading…</div>;
  const monthly = reports.filter((r) => r.kind !== "annual");
  const annual = reports.filter((r) => r.kind === "annual");

  return (
    <div className="space-y-6" data-testid="reports-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-xs text-[#334155] max-w-2xl">
          Monthly reports auto-generated on the 1st @ 06:00 UTC. Annual roll-up on Jan 1 @ 07:00 UTC. Both archived here and emailed per recipient preferences.
        </div>
        {can("retention.manage") && (
          <div className="flex items-center gap-2">
            <button onClick={generateNow} disabled={generating} data-testid="report-generate-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-xs disabled:opacity-50">
              {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} Monthly (prior)
            </button>
            <button onClick={generateAnnual} disabled={generatingAnnual} data-testid="annual-generate-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-xs disabled:opacity-50">
              {generatingAnnual ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Calendar className="w-3.5 h-3.5" />} Annual roll-up
            </button>
          </div>
        )}
      </div>

      {annual.length > 0 && (
        <div>
          <h3 className="font-display font-semibold text-[#0F172A] mb-2 inline-flex items-center gap-2"><Calendar className="w-4 h-4" /> Annual reports</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="annual-list">
            {annual.map((r) => {
              const s = r.report?.compliance_score?.score ?? r.report?.average_score ?? 0;
              const color = s >= 90 ? "#166534" : s >= 80 ? "#1E3A8A" : s >= 60 ? "#92400E" : "#9F1239";
              return (
                <div key={r.id} className="bg-[#FFFBEB] border border-amber-200 rounded-2xl p-4" data-testid={`annual-card-${r.year}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="hw-eyebrow text-[10px]">Year</div>
                      <h3 className="font-display text-lg font-semibold">{r.year}</h3>
                    </div>
                    <div style={{ color }} className="font-display text-3xl font-semibold">{s}</div>
                  </div>
                  <div className="text-[10px] text-[#7E937D] mt-2">Generated {r.generated_at?.slice(0, 10)}</div>
                  {r.delivered_to?.length > 0 && (
                    <div className="text-[10px] text-emerald-700 mt-1">Delivered to {r.delivered_to.length} recipient(s)</div>
                  )}
                  {r.has_pdf && (
                    <div className="mt-3">
                      <button onClick={() => download(r.id, "pdf")} data-testid={`annual-pdf-${r.year}`} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#1E3A8A] text-white">
                        <Download className="w-3 h-3" /> Download PDF
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <h3 className="font-display font-semibold text-[#0F172A] mb-2 inline-flex items-center gap-2"><FileText className="w-4 h-4" /> Monthly reports</h3>
        {monthly.length === 0 ? (
          <div className="bg-white border border-[#E5E5E0] rounded-2xl p-8 text-center text-sm text-[#7E937D]">
            No monthly reports yet. The first one runs on the 1st of next month — or click <strong>Monthly (prior)</strong>.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {monthly.map((r) => {
              const s = r.report?.compliance_score?.score ?? 0;
              const color = s >= 90 ? "#166534" : s >= 80 ? "#1E3A8A" : s >= 60 ? "#92400E" : "#9F1239";
              return (
                <div key={r.id} className="bg-white border border-[#E5E5E0] rounded-2xl p-4" data-testid={`report-card-${r.month}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="hw-eyebrow text-[10px]">Month</div>
                      <h3 className="font-display text-lg font-semibold">{r.month}</h3>
                    </div>
                    <div style={{ color }} className="font-display text-3xl font-semibold">{s}</div>
                  </div>
                  <div className="text-[10px] text-[#7E937D] mt-2">Generated {r.generated_at?.slice(0, 10)}</div>
                  {r.delivered_to?.length > 0 && (
                    <div className="text-[10px] text-emerald-700 mt-1">Delivered to {r.delivered_to.length} recipient(s)</div>
                  )}
                  <div className="mt-3 flex gap-2">
                    {r.has_pdf && (
                      <button onClick={() => download(r.id, "pdf")} data-testid={`report-pdf-${r.month}`} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#1E3A8A] text-white">
                        <Download className="w-3 h-3" /> PDF
                      </button>
                    )}
                    <button onClick={() => download(r.id, "html")} data-testid={`report-html-${r.month}`} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-[#1E3A8A]">
                      <Download className="w-3 h-3" /> HTML
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// =========================================================================
// Compliance score target card (Executive tab)
// =========================================================================

function ComplianceTargetCard({ currentScore }) {
  const { can } = useAuth();
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(false);
  const [target, setTarget] = useState(85);
  const [alertOn, setAlertOn] = useState(true);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/retention/settings");
      setSettings(data);
      setTarget(data.min_compliance_score ?? 85);
      setAlertOn(data.alert_email_below_target !== false);
    } catch (e) { /* non-admins won't load this — that's fine */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!settings) return null;
  const save = async () => {
    try {
      const { data } = await api.patch("/retention/settings", { min_compliance_score: target, alert_email_below_target: alertOn });
      setSettings(data);
      setEditing(false);
      toast.success("Compliance target saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const below = currentScore < (settings.min_compliance_score ?? 85);
  return (
    <div className={`border rounded-2xl p-5 ${below ? "bg-amber-50 border-amber-200" : "bg-white border-[#E5E5E0]"}`} data-testid="compliance-target-card">
      <div className="flex items-center gap-2">
        <Target className={`w-5 h-5 ${below ? "text-amber-700" : "text-[#1E3A8A]"}`} strokeWidth={1.75} />
        <h3 className="font-display font-semibold text-[#0F172A]">Compliance target</h3>
      </div>
      <div className="mt-3 text-sm">
        Minimum acceptable score: <strong data-testid="target-value">{settings.min_compliance_score}</strong>{" "}
        · Current: <strong className={below ? "text-amber-700" : "text-emerald-700"}>{currentScore}</strong>
      </div>
      <div className="mt-1 text-xs text-[#7E937D]">
        Alerts when score drops below target: <strong>{settings.alert_email_below_target !== false ? "on" : "off"}</strong>
      </div>
      {below && (
        <div className="mt-2 text-xs text-amber-800 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Below target — admins notified on the next nightly scan.</div>
      )}
      {can("retention.manage") && !editing && (
        <button onClick={() => setEditing(true)} data-testid="target-edit-btn" className="mt-3 text-[11px] text-[#1E3A8A] underline decoration-dotted">Edit target</button>
      )}
      {editing && (
        <div className="mt-3 space-y-2 text-xs" data-testid="target-edit-form">
          <Field label="Target score (0–100)">
            <input type="number" min={0} max={100} value={target} onChange={(e) => setTarget(parseInt(e.target.value, 10) || 0)} data-testid="target-input" className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0]" />
          </Field>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={alertOn} onChange={(e) => setAlertOn(e.target.checked)} data-testid="target-alert-toggle" />
            Email admins when nightly score drops below this target
          </label>
          <div className="flex gap-2 justify-end pt-2">
            <button onClick={() => setEditing(false)} className="px-3 py-1.5 rounded-full text-xs border border-[#E5E5E0] bg-white">Cancel</button>
            <button onClick={save} data-testid="target-save-btn" className="px-3 py-1.5 rounded-full text-xs bg-[#1E3A8A] text-white">Save</button>
          </div>
        </div>
      )}
    </div>
  );
}

// =========================================================================
// My retention notification preferences card (Executive tab)
// =========================================================================

function MyPrefsCard() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/retention/my-prefs").then(({ data }) => setPrefs(data || {})); }, []);
  if (!prefs) return null;
  const update = async (field, value) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/retention/my-prefs", { [field]: value });
      setPrefs(data);
      toast.success("Preference saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl p-5" data-testid="my-prefs-card">
      <div className="flex items-center gap-2">
        <Bell className="w-5 h-5 text-[#1E3A8A]" strokeWidth={1.75} />
        <h3 className="font-display font-semibold text-[#0F172A]">My retention notifications</h3>
      </div>
      <p className="text-xs text-[#7E937D] mt-1">Just your inbox — independent of the org-wide compliance target above.</p>
      <div className="mt-3 space-y-3 text-sm">
        <PrefRow
          label="Nightly retention digest"
          value={prefs.digest || "on"}
          options={[
            { value: "on", label: "Email me when anything is due" },
            { value: "off", label: "Don't email me" },
          ]}
          onChange={(v) => update("digest", v)}
          testIdPrefix="pref-digest"
          disabled={saving}
        />
        <PrefRow
          label="Monthly & annual compliance reports"
          value={prefs.compliance_report || "pdf_attached"}
          options={[
            { value: "pdf_attached", label: "Email me the full PDF (default)" },
            { value: "summary_only", label: "Email me a one-glance summary instead" },
            { value: "off", label: "Don't email me" },
          ]}
          onChange={(v) => update("compliance_report", v)}
          testIdPrefix="pref-report"
          disabled={saving}
        />
        <PrefRow
          label="Score-dropped-below-target alerts"
          value={prefs.compliance_alert || "on"}
          options={[
            { value: "on", label: "Email me when the score drops below target" },
            { value: "off", label: "Don't email me" },
          ]}
          onChange={(v) => update("compliance_alert", v)}
          testIdPrefix="pref-alert"
          disabled={saving}
        />
      </div>
    </div>
  );
}

function PrefRow({ label, value, options, onChange, testIdPrefix, disabled }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-[#1E3A8A] mb-1">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} data-testid={`${testIdPrefix}-select`} className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm bg-white">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

// =========================================================================
// Bulk override CSV import (Overrides tab)
// =========================================================================

function BulkImportButton({ onComplete }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async () => {
    if (!file) { toast.error("Pick a CSV file first"); return; }
    setImporting(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/retention/overrides/bulk-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      if (data.imported > 0) toast.success(`Imported ${data.imported} override(s)`);
      if (data.errors?.length) toast.error(`${data.errors.length} row(s) had errors`);
      onComplete?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImporting(false); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="bulk-import-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-[#E5E5E0] text-[#1E3A8A] text-xs">
        <Upload className="w-3.5 h-3.5" /> Bulk import CSV
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0F172A]/40" onClick={() => setOpen(false)} data-testid="bulk-import-dialog">
          <div className="bg-white rounded-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-[#E5E5E0] flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold">Bulk import overrides</h2>
              <button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <p className="text-xs text-[#334155]">
                Upload a CSV with header:
              </p>
              <code className="block text-[10px] bg-[#F9F8F6] border border-[#E5E5E0] rounded px-2 py-1 font-mono break-all">
                collection,record_id,override_type,new_retention_until,justification
              </code>
              <p className="text-xs text-[#7E937D]">
                <strong>override_type</strong> must be <code>extend</code> or <code>early_destroy</code>.{" "}
                <strong>new_retention_until</strong> is required for <code>extend</code> (ISO date, e.g. 2030-12-31).{" "}
                <strong>justification</strong> ≥ 10 chars. Each row creates a <em>proposed</em> override; PHI types still need a second admin to approve.
              </p>
              <input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="bulk-import-file" className="block w-full text-xs" />
              {result && (
                <div className="mt-3 p-3 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0] text-xs space-y-1" data-testid="bulk-import-result">
                  <div><strong className="text-emerald-700">{result.imported}</strong> imported as proposed</div>
                  <div><strong className="text-amber-700">{result.skipped_duplicates}</strong> skipped (duplicate overrides)</div>
                  {result.errors?.length > 0 && (
                    <div>
                      <div><strong className="text-red-700">{result.errors.length}</strong> rows with errors:</div>
                      <ul className="mt-1 max-h-32 overflow-y-auto" data-testid="bulk-import-errors">
                        {result.errors.slice(0, 20).map((er, i) => (
                          <li key={i} className="text-[10px] text-red-700">row {er.row}: {er.message}</li>
                        ))}
                        {result.errors.length > 20 && <li className="text-[10px] text-[#7E937D]">…+{result.errors.length - 20} more</li>}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="px-5 py-4 bg-[#F9F8F6] border-t border-[#E5E5E0] flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-xs border border-[#E5E5E0] bg-white">Close</button>
              <button onClick={submit} disabled={importing || !file} data-testid="bulk-import-submit" className="px-4 py-2 rounded-full text-xs bg-[#1E3A8A] text-white disabled:opacity-50">
                {importing ? "Importing…" : "Import"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
